
/**
 * Module dependencies.
 */

import { DownloadCategory } from 'src/types/downloads';
import { ProductApplication } from 'src/types/products';
import { applications } from 'src/core/collections-config/applications';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { categories, categoriesValues } from 'src/core/projects-config/categories';
import { categoryNameToSlug } from 'src/core/utils/category-name-slug';
import { effects } from 'src/core/collections-config/effects';
import { getApplicationsSorted } from 'src/core/utils/sort-applications';
import { myRevigresRoutes } from 'src/core/routes';
import { availableFiltersKeys as projectsFiltersKeys } from 'src/core/projects-config/filters';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useMemo } from 'react';
import { useTranslate } from 'src/core/utils/translator';
import compact from 'lodash/compact';
import facebookIcon from 'src/assets/svg/facebook.svg';
import filter from 'lodash/filter';
import find from 'lodash/find';
import instagramIcon from 'src/assets/svg/instagram.svg';
import kebabCase from 'lodash/kebabCase';
import linkedinIcon from 'src/assets/svg/linkedin.svg';
import map from 'lodash/map';
import pinterestIcon from 'src/assets/svg/pinterest.svg';
import qs from 'qs';
import useConfigurations from 'src/api/app/configurations/use-configurations';
import useDownloadCategories from 'src/api/app/downloads/use-download-categories';
import useProductApplications from 'src/api/app/products/use-product-applications';
import vimeoIcon from 'src/assets/svg/vimeo.svg';
import youtubeIcon from 'src/assets/svg/youtube.svg';

/**
 * Export `Name` type.
 */

export type Name = 'collections' | 'company' | 'inspiration' | 'tools';

/**
 * Export `MenuLink` type.
 */

export type MenuLink = {
  href: string,
  icon?: string,
  image?: string | null,
  label: string
};

/**
 * Export `MenuGroup` type.
 */

export type MenuGroup = {
  links: MenuLink[],
  title: string
};

/**
 * Export `Menu` type.
 */

export type Menu = {
  cta?: MenuLink | null,
  groups: MenuGroup[],
  name: Name,
  title: string
};

/**
 * Applications listed under `Coleções`, in the order defined in the design.
 *
 * The labels and the ids used to filter the collections list are resolved from
 * the API, so they stay localized. The API serves `externalIdentifier` in
 * kebab case while the shared config holds it in camel case, hence the
 * conversion.
 */

const featuredApplications = map([
  applications.bathrooms,
  applications.kitchens,
  applications.pools,
  applications.publicSpaces,
  applications.outdoorFloorTiles
], kebabCase);

/**
 * Effects listed under `Coleções`, in the order defined in the design.
 */

const featuredEffects = [
  effects.unicolor,
  effects.urban,
  effects.stone,
  effects.marble,
  effects.wood
];

/**
 * Resolve the image shown in the mega menu while a link is hovered.
 *
 * Static on purpose: each is an ambience of one of the first collections the
 * filter lists, or of the highlighted collection, copied at a resolution that
 * fits the image block. The filters' own images are too small for it, and
 * pointing at the cms media would break whenever it is replaced there.
 */

function getNavbarImage(type: 'applications' | 'effects' | 'highlights', key: string): string {
  return assetUrlResolve(`/static/images/navbar/${type}/${key}.jpg`);
}

/**
 * Project categories listed under `Inspiração`, in the order defined in the
 * design.
 */

const featuredCategories = [
  categories.residentialBuildings,
  categories.cultureAndSports,
  categories.hotelsAndRestaurants,
  categories.publicSpaces
];

/**
 * `LocalizedSlug` type.
 *
 * A single slug when the CMS uses the same one everywhere, or one per locale
 * when it is translated. `null` means the page does not exist yet.
 */

type LocalizedSlug = string | Record<string, string> | null;

/**
 * `FeaturedHighlight` type.
 */

type FeaturedHighlight = {
  image?: string,
  label: string,
  slug?: LocalizedSlug,
  type: 'collection' | 'collections' | 'landingPage' | 'lightXl' | 'tech20mm'
};

/**
 * Entries listed under `Coleções` › `Destaque`.
 *
 * Editorial picks, deliberately static: they are swapped whenever the client
 * asks for it. Some are product collections, others are CMS landing pages and
 * others are routes of their own (no `slug`), such as the product listing
 * pages, hence the explicit `type`. Brand names are not translated, hence the
 * literal labels. An entry whose slug does not resolve is simply not rendered,
 * which is preferable to shipping a dead link.
 *
 * The `image` is a file under `/static/images/navbar/highlights`: an
 * ambience of the collection itself or, for the product listing pages, of a
 * collection holding the products they list.
 */

const featuredHighlights: FeaturedHighlight[] = [
  { image: 'light-xl', label: 'Light XL', type: 'lightXl' },
  { image: 'tech-20mm', label: 'Tech 20 mm', type: 'tech20mm' },
  { label: 'Revigrés Contract', slug: 'revigres-contract', type: 'landingPage' },
  { image: 'cromatica', label: 'Cromática', slug: 'cromatica', type: 'collection' },
  { image: 'omni', label: 'Omni', slug: 'omni', type: 'collection' }
];

/**
 * `Revigrés B2B` url per locale.
 *
 * The B2B platform only has Portuguese, English and French versions, the
 * other locales fall back to English.
 */

const b2bUrls: Record<string, string> = {
  en: 'https://pro.revigres.pt/en/',
  fr: 'https://pro.revigres.pt/fr/',
  pt: 'https://pro.revigres.pt/pt/'
};

/**
 * Catalogues download category id.
 *
 * The downloads page selects a category by the kebab cased name the API
 * returns, which is translated (`catalogos`, `catalogues`, ...), so the link
 * is built from the category itself rather than from a fixed slug.
 */

const cataloguesCategoryId = 'e791863b-8dfd-470f-8f01-8e85911da801';

/**
 * `Vídeos` url, the YouTube shorts channel listed in the footer.
 */

const videosUrl = 'https://www.youtube.com/@revigrescom/shorts';

/**
 * `Sistema 3D` slug per locale.
 *
 * A CMS landing page served by the catch all `/:slug` route, so it has no
 * entry of its own in `i18n-routes` and the slug is translated.
 */

const system3dSlugs: LocalizedSlug = {
  de: '3d-system',
  en: '3d-system',
  es: '3d-system',
  fr: 'systeme-3d',
  pt: 'sistema-3d'
};

/**
 * Menu data stale time.
 *
 * The taxonomies behind the menu barely change, so they are never refetched
 * during a session — without this the navbar would hit the API again on every
 * window focus.
 */

const staleTime = 30 * 60 * 1000;

/**
 * Resolve a possibly localized slug.
 */

function resolveSlug(slug: LocalizedSlug, locale: string): string | null {
  if (!slug) {
    return null;
  }

  return typeof slug === 'string' ? slug : slug[locale] ?? null;
}

/**
 * Build a link to a listing filtered by a single value, in the
 * `filter[key]=value` shape the listings' filters read from the url.
 */

function filterLink(href: string | null, key: string, value: string): string | null {
  return href ? `${href}?${qs.stringify({ filter: { [key]: value } })}` : null;
}

/**
 * Build a menu group, dropping links that could not be resolved.
 */

function buildGroup(title: string, links: Array<MenuLink | null>): MenuGroup | null {
  const resolvedLinks = compact(links);

  return resolvedLinks.length > 0 ? { links: resolvedLinks, title } : null;
}

/**
 * Build a menu link, dropping it when the route does not exist in the current
 * locale.
 */

function buildLink(href: string | null | undefined, label: string, image?: string | null): MenuLink | null {
  return href ? { href, image, label } : null;
}

/**
 * Export `useFooterLinks`.
 *
 * The legal links in the footer credits row, in the order defined in the
 * design.
 */

export function useFooterLinks(): MenuLink[] {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { data: configurations } = useConfigurations({ staleTime });

  return useMemo(() => compact([
    buildLink(routeResolver('privacyPolicy'), translate('menu:privacyPolicy')),
    buildLink(routeResolver('cookiesPolicy'), translate('menu:cookiesPolicy')),
    buildLink(routeResolver('termsConditions'), translate('menu:termsConditions')),
    buildLink(configurations?.complainBookUrl, translate('menu:complainBook')),
    buildLink(routeResolver('reportingChannel'), translate('menu:reportingChannel')),
    buildLink(configurations?.complimentBookUrl, translate('menu:complimentBook'))
  ]), [configurations, routeResolver, translate]);
}

/**
 * Export `useFooterSocialLinks`.
 *
 * Listed with the network's icon before its name, in the order defined in the
 * design, which is not the order the api returns them in.
 */

export function useFooterSocialLinks(): MenuLink[] {
  const { data: configurations } = useConfigurations({ staleTime });

  return useMemo(() => compact(map([
    { href: configurations?.instagram, icon: instagramIcon, label: 'Instagram' },
    { href: configurations?.facebook, icon: facebookIcon, label: 'Facebook' },
    { href: configurations?.linkedin, icon: linkedinIcon, label: 'Linkedin' },
    { href: configurations?.youtube, icon: youtubeIcon, label: 'Youtube' },
    { href: configurations?.vimeo, icon: vimeoIcon, label: 'Vimeo' },
    { href: configurations?.pinterest, icon: pinterestIcon, label: 'Pinterest' }
  ], ({ href, icon, label }) => href ? { href, icon, label } : null)), [configurations]);
}

/**
 * Export `useFooterMenu`.
 *
 * The five link columns of the footer. They deliberately do not mirror
 * `useMenu()`: the footer lists every application rather than the featured
 * ones, and the `Marca` column holds entries the navbar does not have.
 */

export function useFooterMenu(): MenuGroup[] {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { data: productApplications } = useProductApplications<ProductApplication[]>({ staleTime });

  return useMemo(() => {
    const system3dSlug = resolveSlug(system3dSlugs, locale);
    const applicationLinks = map(
      getApplicationsSorted(productApplications ?? []),
      ({ name, slug }) => buildLink(routeResolver('application', { slug }), name)
    );

    return compact([
      buildGroup(translate('footer:applications.title'), applicationLinks),
      buildGroup(translate('menu:inspiration'), [
        buildLink(routeResolver('storytiling'), translate('menu:inspirationMenu.storytiling')),
        buildLink(routeResolver('portfolio'), translate('menu:inspirationMenu.portfolio')),
        buildLink(videosUrl, translate('menu:inspirationMenu.videos'))
      ]),
      buildGroup(translate('menu:tools'), [
        // eslint-disable-next-line no-script-url
        buildLink('javascript:roomvo.startStandaloneVisualizer();', translate('menu:toolsMenu.arDesign')),
        buildLink(
          system3dSlug ? routeResolver('landingPage', { slug: system3dSlug }) : null,
          translate('menu:toolsMenu.system3d')
        ),
        buildLink(routeResolver('toolsBimObjects'), translate('menu:toolsMenu.bim')),
        buildLink(routeResolver('faqs'), translate('menu:toolsMenu.faqs')),
        buildLink(myRevigresRoutes.home, translate('menu:toolsMenu.myRevigres')),
        buildLink(b2bUrls[locale] ?? b2bUrls.en, translate('menu:toolsMenu.b2b')),
        buildLink(routeResolver('toolsProductComparator'), translate('menu:toolsMenu.comparator')),
        buildLink(routeResolver('recommend'), translate('menu:toolsMenu.recommender')),
        buildLink(routeResolver('whereToBuy'), translate('menu:whereToBuy'))
      ]),
      buildGroup(translate('menu:companyMenu.brand'), [
        buildLink(routeResolver('aboutUs'), translate('menu:companyMenu.aboutUs')),
        buildLink(routeResolver('certificates'), translate('menu:toolsMenu.certificates')),
        // `Política de Sustentabilidade` has no destination yet. The client will
        // either point it at an existing route or create the page in the cms;
        // until then it is not rendered, which is preferable to shipping a dead
        // link.
        buildLink(null, translate('menu:companyMenu.sustainabilityPolicy')),
        buildLink(routeResolver('history'), translate('menu:companyMenu.history')),
        buildLink(routeResolver('innovation'), translate('menu:companyMenu.innovation')),
        buildLink(routeResolver('environment'), translate('menu:companyMenu.environment')),
        buildLink(routeResolver('socialResponsability'), translate('menu:companyMenu.socialResponsability')),
        buildLink(routeResolver('media'), translate('menu:companyMenu.media')),
        buildLink(routeResolver('careers'), translate('menu:companyMenu.recruitment')),
        buildLink(routeResolver('fundedProjects'), translate('menu:companyMenu.fundedProjects'))
      ])
    ]);
  }, [locale, productApplications, routeResolver, translate]);
}

/**
 * Export `useMenu`.
 */

export function useMenu(): Menu[] {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const { data: configurations } = useConfigurations({ staleTime });
  const { data: downloadCategories } = useDownloadCategories<DownloadCategory[]>({ staleTime });
  const { data: productApplications } = useProductApplications<ProductApplication[]>({ staleTime });

  return useMemo(() => {
    const collectionsHref = routeResolver('collections');
    const downloadsHref = routeResolver('downloads');
    const portfolioHref = routeResolver('portfolio');
    const system3dSlug = resolveSlug(system3dSlugs, locale);
    const cataloguesCategory = find(downloadCategories, { id: cataloguesCategoryId });
    const highlightLinks = map(featuredHighlights, ({ image, label, slug, type }) => {
      const resolvedSlug = resolveSlug(slug, locale);

      if ((slug !== undefined && !resolvedSlug) || !routeResolver(type)) {
        return null;
      }

      return buildLink(
        routeResolver(type, { slug: resolvedSlug }),
        label,
        image ? getNavbarImage('highlights', image) : null
      );
    });

    const applicationLinks = map(featuredApplications, externalIdentifier => {
      const application = find(productApplications, {
        externalIdentifier
      }) as ProductApplication | undefined;

      if (!application) {
        return null;
      }

      return buildLink(
        filterLink(collectionsHref, availableFiltersKeys.application, application.id),
        application.name,
        getNavbarImage('applications', externalIdentifier)
      );
    });

    const menu: Array<Menu | null> = [{
      cta: buildLink(collectionsHref, translate('menu:collectionsMenu.cta')),
      groups: compact([
        buildGroup(translate('menu:collectionsMenu.application'), applicationLinks),
        buildGroup(translate('menu:collectionsMenu.effect'), map(featuredEffects, effect => buildLink(
          filterLink(collectionsHref, availableFiltersKeys.effect, effect),
          translate(`collections:effects.${effect}`),
          getNavbarImage('effects', effect)
        ))),
        buildGroup(translate('menu:collectionsMenu.highlight'), highlightLinks)
      ]),
      name: 'collections',
      title: translate('menu:collections')
    }, {
      groups: compact([
        buildGroup(translate('menu:inspirationMenu.spaces'), [
          buildLink(routeResolver('inspiration'), translate('menu:inspirationMenu.ambients')),
          buildLink(portfolioHref, translate('menu:inspirationMenu.portfolio'))
        ]),
        buildGroup(translate('menu:inspirationMenu.news'), [
          buildLink(routeResolver('storytiling'), translate('menu:inspirationMenu.storytiling'))
        ]),
        buildGroup(translate('menu:inspirationMenu.highlight'), map(featuredCategories, category => buildLink(
          filterLink(portfolioHref, projectsFiltersKeys.category, categoriesValues[category]),
          translate(`projects:categories.${category}`)
        )))
      ]),
      name: 'inspiration',
      title: translate('menu:inspiration')
    }, {
      groups: compact([
        buildGroup(translate('menu:toolsMenu.simulators'), [
          // eslint-disable-next-line no-script-url
          buildLink('javascript:roomvo.startStandaloneVisualizer();', translate('menu:toolsMenu.arDesign')),
          buildLink(
            system3dSlug ? routeResolver('landingPage', { slug: system3dSlug }) : null,
            translate('menu:toolsMenu.system3d')
          )
        ]),
        buildGroup(translate('menu:toolsMenu.tools'), [
          buildLink(routeResolver('recommend'), translate('menu:toolsMenu.recommender')),
          buildLink(routeResolver('toolsProductComparator'), translate('menu:toolsMenu.comparator')),
          buildLink(myRevigresRoutes.home, translate('menu:toolsMenu.myRevigres')),
          buildLink(b2bUrls[locale] ?? b2bUrls.en, translate('menu:toolsMenu.b2b'))
        ]),
        buildGroup(translate('menu:toolsMenu.downloads'), [
          buildLink(downloadsHref, translate('menu:toolsMenu.downloads')),
          buildLink(
            downloadsHref && cataloguesCategory ? `${downloadsHref}?${qs.stringify({
              category: categoryNameToSlug(cataloguesCategory.name)
            })}` : null,
            translate('menu:toolsMenu.catalogues')
          ),
          buildLink(routeResolver('certificates'), translate('menu:toolsMenu.certificates')),
          buildLink(routeResolver('toolsBimObjects'), translate('menu:toolsMenu.bim')),
          buildLink(routeResolver('faqs'), translate('menu:toolsMenu.faqs'))
        ])
      ]),
      name: 'tools',
      title: translate('menu:tools')
    }, {
      groups: compact([
        buildGroup(translate('menu:companyMenu.brand'), [
          buildLink(routeResolver('aboutUs'), translate('menu:companyMenu.aboutUs')),
          buildLink(routeResolver('history'), translate('menu:companyMenu.history')),
          buildLink(routeResolver('innovation'), translate('menu:companyMenu.innovation')),
          buildLink(routeResolver('media'), translate('menu:companyMenu.media')),
          buildLink(routeResolver('contacts'), translate('menu:contacts'))
        ]),
        buildGroup(translate('menu:companyMenu.trust'), [
          buildLink(routeResolver('environment'), translate('menu:companyMenu.environment')),
          buildLink(routeResolver('socialResponsability'), translate('menu:companyMenu.socialResponsability')),
          buildLink(routeResolver('fundedProjects'), translate('menu:companyMenu.fundedProjects')),
          buildLink(configurations?.complimentBookUrl, translate('menu:complimentBook')),
          buildLink(configurations?.complainBookUrl, translate('menu:complainBook'))
        ]),
        buildGroup(translate('menu:companyMenu.careers'), [
          buildLink(routeResolver('careers'), translate('menu:companyMenu.recruitment')),
          buildLink(routeResolver('reportingChannel'), translate('menu:reportingChannel'))
        ])
      ]),
      name: 'company',
      title: translate('menu:company')
    }];

    return filter(compact(menu), ({ groups }) => groups.length > 0);
  }, [configurations, downloadCategories, locale, productApplications, routeResolver, translate]);
}
